import type { ListingsModel } from '@padspin/firestore-listings';
import { regionCode } from './region-code';

/** Listing fields the suffix is derived from. */
export type ListingSeoInput = Pick<
  ListingsModel,
  'bedrooms' | 'neighborhood' | 'place_names' | 'city' | 'state'
>;

/** The listing fields the suffix is derived from, in a fixed order. */
export const LISTING_SEO_INPUT_FIELDS = [
  'bedrooms',
  'neighborhood',
  'place_names',
  'city',
  'state',
] as const satisfies readonly (keyof ListingSeoInput)[];

/**
 * True when a field the listing's URL suffix is derived from differs between
 * `before` and `after`. Never looks at the stored suffix itself.
 */
export function slugInputsChanged(
  before: Partial<ListingSeoInput>,
  after: Partial<ListingSeoInput>,
): boolean {
  return LISTING_SEO_INPUT_FIELDS.some(
    (field) => JSON.stringify(before[field]) !== JSON.stringify(after[field]),
  );
}

const MAX_LENGTH = 80;

// Callers pass raw Firestore data (sitemap, backfill), where legacy listings
// can hold non-string values in these fields.
function text(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function normalize(text: string): string {
  return text
    .replace(/&/g, ' and ')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function bedroomPart(bedrooms: number | null | undefined): string | null {
  if (typeof bedrooms !== 'number' || !Number.isFinite(bedrooms)) {
    return null;
  }
  if (bedrooms < 0) {
    return null;
  }
  if (bedrooms === 0) {
    return 'studio';
  }
  return bedrooms === 1 ? '1 bed' : `${bedrooms} beds`;
}

function truncate(suffix: string): string {
  if (suffix.length <= MAX_LENGTH) {
    return suffix;
  }
  const cut = suffix.slice(0, MAX_LENGTH + 1);
  const lastHyphen = cut.lastIndexOf('-');
  const trimmed =
    lastHyphen > 0 ? cut.slice(0, lastHyphen) : suffix.slice(0, MAX_LENGTH);
  return trimmed.replace(/-+$/g, '');
}

/**
 * Derives the URL suffix for a listing, `<n>-beds-<neighborhood>-<city>-<state>`,
 * e.g. `2-beds-bellingham-square-chelsea-ma`. The state is left out when it is
 * not a recognised state, province or territory, or when the city already
 * ends in its code (legacy listings with `city: 'NY'` or `'New York, NY'`).
 *
 * The suffix is decoration only: never parse it, compare it, or use it to
 * identify a listing. Never throws; returns `''` when nothing is derivable.
 */
export function listingSeoSlug(listing: Partial<ListingSeoInput>): string {
  const city = text(listing.city) ?? '';
  const neighborhood =
    text(listing.neighborhood) ??
    (Array.isArray(listing.place_names) ? text(listing.place_names[0]) : null);
  const includeNeighborhood =
    !!neighborhood && normalize(neighborhood) !== normalize(city);
  const state = regionCode(listing.state)?.toLowerCase();
  const normalizedCity = normalize(city);
  const includeState =
    !!state &&
    normalizedCity !== state &&
    !normalizedCity.endsWith(`-${state}`);
  const parts = [
    bedroomPart(listing.bedrooms),
    includeNeighborhood ? neighborhood : null,
    city,
    includeState ? state : null,
  ].filter((part): part is string => !!part);
  return truncate(normalize(parts.join(' ')));
}
